import differenceWith from 'lodash/differenceWith'
import orderBy from 'lodash/orderBy'

import { MAX_REMOTE_VIDEOS_ON } from 'constants/config'
import { getParticipantIndex, isTeacher } from 'helpers/twilio'
import * as constants from 'services/webSockets/roomState/constants'

import * as types from '../actionTypes/Room'
import { updateObject } from '../utility'

const setParticipantDefaultAttributes = ({
    id = null,
    name = null,
    isVideoEnabled = false,
    isAudioEnabled = false,
    isScreenSharing = false,
    isElsewhere = false,
}) => ({
    id,
    name,
    isVideoEnabled,
    isAudioEnabled,
    isScreenSharing,
    isElsewhere,
})

export const initialState = {
    roomName: null,
    master: null, // uuid of participant who will send talktime data
    isMaster: false, // checks if the current user is the the master talktime data sender
    roomState: constants.VIDEO_CALL, // possible room states < quiz | screen_share | whiteboard >
    isTeacherPresent: false,
    isQuizMode: false,
    isWhiteboardMode: false,
    isScreenshareMode: false,
    isSpeaking: false, // set to true when volumeLevel is above 15 in AudioLevelIndicator
    studentsCount: 0,
    isRecording: false,
    isInteractionMode: 'fun',
    isLoading: true,
    classTitle: '',
    roomType: '',
    participants: [],
    dominantSpeaker: null,
    latestDisconnectedParticipant: {},
    latestConnectedParticipant: {},
    isNetworkBandwidthSaverMode: false,
    isActivityMode: false,
}

const setParticipant = (state, action) => {
    const { participant: participantData } = action

    const isParticipantAvailable =
        getParticipantIndex(state.participants, participantData.id) > -1

    if (isParticipantAvailable) return state

    return {
        ...state,
        participants: [
            ...state.participants,
            setParticipantDefaultAttributes(participantData),
        ],
    }
}

const updateParticipantInfo = (state, action) => {
    const { id, infoKey, infoValue } = action

    const participantIndex = getParticipantIndex(state.participants, id)

    const localUserIdentity = window.twilioRoom?.localParticipant?.identity

    if (participantIndex < 0) return state

    const updatedParticipants = [
        ...state.participants.slice(0, participantIndex),
        {
            ...state.participants[participantIndex],
            [infoKey]: infoValue,
        },
        ...state.participants.slice(participantIndex + 1),
    ]

    /**
     * we dont require sorting in case of video event change for local user
     */
    if (!localUserIdentity?.startsWith(id) && infoKey === 'isVideoEnabled') {
        // require sorting here
        return {
            ...state,
            participants: orderBy(
                updatedParticipants,
                'isVideoEnabled',
                'desc'
            ),
        }
    }

    return {
        ...state,
        participants: updatedParticipants,
    }
}

const setRemoteParticipants = (state, action) => {
    const { participants } = action

    /**
     * check if participant is already available in state before
     * as this can happen if this is the case of reconnecting
     */

    const unchangedParticipants = differenceWith(
        state.participants,
        participants,
        (x, y) => x.id === y.id
    )

    return {
        ...state,
        participants: orderBy(
            [
                ...unchangedParticipants,
                ...participants.map(participant =>
                    setParticipantDefaultAttributes(participant)
                ),
            ],
            'isVideoEnabled',
            'desc'
        ),
    }
}

const removeParticipant = (state, action) => {
    const {
        participant: { id },
    } = action

    const participantIndex = getParticipantIndex(state.participants, id)

    if (participantIndex < 0) return state

    return {
        ...state,
        participants: [
            ...state.participants.slice(0, participantIndex),
            ...state.participants.slice(participantIndex + 1),
        ],
    }
}

export const handleDominantSpeakerChanged = (state, action) => {
    const dominantSpeakerId = action.value

    const localUserIdentity = window.twilioRoom?.localParticipant?.identity

    /**
     * execute this only if dominant speaker is remote user
     * as local participant always reside in first page
     * so better don't compute other logic if that is the case
     **/
    if (
        dominantSpeakerId &&
        !localUserIdentity?.startsWith(dominantSpeakerId)
    ) {
        // first find index of dominant speaker , if its not on first page, then only change order
        const dominantSpeakerCurrentIndex = getParticipantIndex(
            state.participants,
            dominantSpeakerId
        )

        const { isActivityMode, isTeacherPresent } = state
        const isLocalUserIsTeacher = isTeacher(localUserIdentity)

        /**
         * first check if teacher is available or not, if not then we
         * need to chagge condition as teacher position is fixed
         * isTeacherPresent -> True -> dominantSpeakerCurrentIndex >= MAX_REMOTE_VIDEOS_ON
         * isTeacherPresent -> False -> dominantSpeakerCurrentIndex >= (MAX_REMOTE_VIDEOS_ON - 1)
         *
         * isLocalUserIsTeacher -> True -> 4
         * isLocalUserIsTeacher -> False -> 3
         */

        const enableShuffling =
            (isActivityMode &&
                dominantSpeakerCurrentIndex >=
                    (isLocalUserIsTeacher ? 4 : 3)) ||
            (!isActivityMode &&
                dominantSpeakerCurrentIndex >=
                    (isTeacherPresent
                        ? MAX_REMOTE_VIDEOS_ON
                        : MAX_REMOTE_VIDEOS_ON - 1))

        if (enableShuffling) {
            // keep dominantSpeaker on top, and rest below
            return {
                ...state,
                participants: [
                    state.participants[dominantSpeakerCurrentIndex],
                    ...state.participants.slice(0, dominantSpeakerCurrentIndex),
                    ...state.participants.slice(
                        dominantSpeakerCurrentIndex + 1
                    ),
                ],
                dominantSpeaker: dominantSpeakerId,
            }
        }
    }

    return {
        ...state,
        dominantSpeaker: dominantSpeakerId,
    }
}

const setRoomState = ({ master, state, teacher_present, userId }) => {
    const isQuizMode = state === constants.QUIZ
    const isScreenshareMode = state === constants.SCREEN_SHARE
    const isWhiteboardMode = state === constants.WHITEBOARD

    return {
        master,
        isMaster: userId === master,
        state,
        isTeacherPresent: teacher_present,
        isQuizMode,
        isWhiteboardMode,
        isScreenshareMode,
    }
}

const setDisconnectedParticipant = (state, action) => {
    return {
        ...state,
        latestDisconnectedParticipant: action.data,
    }
}

const setConnectedParticipant = (state, action) => {
    return {
        ...state,
        latestConnectedParticipant: action.data,
    }
}

const Room = (state = initialState, action) => {
    switch (action.type) {
        case types.SET_ROOM_NAME:
            return {
                ...state,
                roomName: action.payload,
            }

        case types.SET_ROOM_STATE:
            return {
                ...state,
                ...setRoomState(action.payload),
            }

        case types.SET_MASTER:
            return {
                ...state,
                ...action.payload,
            }

        case types.SET_IS_SPEAKING:
            return updateObject(state, {
                isSpeaking: action.isSpeaking,
            })

        case types.SET_STUDENTS_COUNT:
            return updateObject(state, {
                studentsCount: action.studentsCount,
            })

        case types.SET_RECORDING_ALERT:
            return {
                ...state,
                isRecording: action.alert,
            }

        case types.SET_IS_INTERACTION_MODE:
            return updateObject(state, {
                isInteractionMode: action.isInteractionMode,
            })

        case types.SET_TWILIO_TOKEN:
            return {
                ...state,
                twilioToken: action.payload,
            }

        case types.SET_IS_ROOM_LOADING:
            return {
                ...state,
                isLoading: action.payload,
            }

        case types.SET_CLASS_TITLE_AND_ROOM_TYPE:
            const { classTitle, roomType } = action.payload
            return {
                ...state,
                classTitle,
                roomType,
            }

        case types.SET_PARTICIPANT:
            return setParticipant(state, action)

        case types.UPDATE_PARTICIPANT_INFO:
            return updateParticipantInfo(state, action)

        case types.REMOVE_PARTICIPANT:
            return removeParticipant(state, action)

        case types.SET_REMOTE_PARTICIPANTS:
            return setRemoteParticipants(state, action)

        case types.UPDATE_DOMINANT_SPEAKER:
            return handleDominantSpeakerChanged(state, action)

        case types.UPDATE_DISCONNECTED_PARTICIPANT:
            return setDisconnectedParticipant(state, action)

        case types.UPDATE_CONNECTED_PARTICIPANT:
            return setConnectedParticipant(state, action)

        case types.UPDATE_NETWORK_BANDWIDTH_SAVER_MODE:
            return {
                ...state,
                isNetworkBandwidthSaverMode: action.value,
            }

        case types.UPDATE_IS_TEACHER_PRESENT:
            return {
                ...state,
                isTeacherPresent: action.value,
            }

        case types.UPDATE_IS_ACTIVITY_MODE:
            return {
                ...state,
                isActivityMode: action.value,
            }

        default:
            return state
    }
}

export default Room
